import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ---------------------------------------------------------------------------
// Shared constants
// ---------------------------------------------------------------------------

export const AGENT_CREATION_MODEL_ID = "claude-opus-4-8" as const;

// ---------------------------------------------------------------------------
// Shared sub-schemas
// ---------------------------------------------------------------------------

const ThreadStatusSchema = z.enum(["IN_PROGRESS", "COMPLETED", "FAILED", "CANCELLED"]);

const AgentCreationMessageSchema = z.object({
  id: z.string(),
  content: z.string(),
  role: z.string(),
  timestamp: z.string().optional()
});

const AgentCreationThreadSummarySchema = z.object({
  id: z.string(),
  status: ThreadStatusSchema,
  agentId: z.string().nullable(),
  agentName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
});

/**
 * ZAgentCreationAssistant - Zod Validation Schemas for Agent Creation Assistant API
 *
 * All 8 endpoints under @Controller("agents/assistant")
 */
export const ZAgentCreationAssistant = {
  /**
   * POST /api/agents/assistant/configureAssistantWithMessage
   * Send a message to the agent creation assistant.
   * Response can be either a simple completion { runId } or an array of tool calls.
   */
  ConfigureAssistantWithMessage: {
    method: "POST" as const,
    path: "/api/agents/assistant/configureAssistantWithMessage",
    Body: z.object({
      message: z.string(),
      threadId: z.string().optional(),
      agentId: z.string().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        threadId: z.string(),
        response: z.union([
          z.object({ runId: z.string() }),
          z.array(z.object({ runId: z.string(), tool: z.string() }))
        ])
      })
    )
  },

  /**
   * POST /api/agents/assistant/getPageMessageFromThreadId
   * Retrieve all messages in a conversation thread.
   */
  GetPageMessageFromThreadId: {
    method: "POST" as const,
    path: "/api/agents/assistant/getPageMessageFromThreadId",
    Body: z.object({
      threadId: z.string()
    }),
    Response: ApiSuccessSchema(
      z.object({
        messages: z.array(AgentCreationMessageSchema)
      })
    )
  },

  /**
   * POST /api/agents/assistant/getRun
   * Get the status of an OpenAI run by run ID and thread ID.
   * Uses z.unknown() because the Run type is an OpenAI SDK type.
   */
  GetRun: {
    method: "POST" as const,
    path: "/api/agents/assistant/getRun",
    Body: z.object({
      runId: z.string(),
      threadId: z.string()
    }),
    Response: ApiSuccessSchema(
      z.object({
        run: z.unknown()
      })
    )
  },

  /**
   * POST /api/agents/assistant/stopRunByThreadId
   * Cancel the current agent creation run.
   */
  StopRunByThreadId: {
    method: "POST" as const,
    path: "/api/agents/assistant/stopRunByThreadId",
    Body: z.object({
      threadId: z.string()
    }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.literal(true)
      })
    )
  },

  /**
   * POST /api/agents/assistant/getConfig
   * Retrieve the current agent configuration JSON from a thread.
   * Uses z.unknown() because the config is a free-form JSON object.
   */
  GetConfig: {
    method: "POST" as const,
    path: "/api/agents/assistant/getConfig",
    Body: z.object({
      threadId: z.string()
    }),
    Response: ApiSuccessSchema(
      z.object({
        config: z.unknown()
      })
    )
  },

  /**
   * GET /api/agents/assistant/threads
   * List all agent creation threads for the current organization.
   */
  ListThreads: {
    method: "GET" as const,
    path: "/api/agents/assistant/threads",
    Params: z.object({
      limit: z.number().optional(),
      offset: z.number().optional()
    }),
    Response: ApiSuccessSchema(
      z.object({
        threads: z.array(AgentCreationThreadSummarySchema),
        total: z.number()
      })
    )
  },

  /**
   * POST /api/agents/assistant/threads/:threadId/fork
   * Fork a thread from a specific message.
   */
  ForkThread: {
    method: "POST" as const,
    path: "/api/agents/assistant/threads/:threadId/fork",
    PathVars: z.object({ threadId: z.string() }),
    Body: z.object({
      fromMessageId: z.string()
    }),
    Response: ApiSuccessSchema(
      z.object({
        threadId: z.string()
      })
    )
  },

  /**
   * DELETE /api/agents/assistant/threads/:threadId
   * Delete an agent creation thread and all its messages.
   */
  DeleteThread: {
    method: "DELETE" as const,
    path: "/api/agents/assistant/threads/:threadId",
    PathVars: z.object({ threadId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        success: z.boolean()
      })
    )
  },

  /**
   * POST /api/agents/assistant/threads/:threadId/retry-generation
   * Retry failed agent generation for a thread.
   */
  RetryGeneration: {
    method: "POST" as const,
    path: "/api/agents/assistant/threads/:threadId/retry-generation",
    PathVars: z.object({ threadId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({
        agentId: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        role: z.string()
      })
    )
  }
} as const;
